// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinkIcon from "@hardwoods/XDL/Icons/Link";
import color from "@hardwoods/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

interface Props extends WidgetProps, HasCategoryContext {}

interface CategoryDocument {
    id: string;
    name?: string;
    path?: string;
    type?: string;
}

export interface CategoryDocumentsStyles {
    container?: InjectableCss;
    documentLink?: LinkPresentationProps;
}

export const categoryDocumentsStyles: CategoryDocumentsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            flex-shrink: 0;
            flex-basis: auto;
            padding: 20px;
            border: 1px solid ${color("common.border")};
            border-radius: 10px;
            max-width: 345px;
            min-width: 180px;
            > ${StyledButton}:last-child {
                margin-bottom: 0;
            }
        `,
    },
    documentLink: {
        color: "text.main",
        icon: {
            position: "left",
            iconProps: {
                src: LinkIcon,
                size: 14,
            },
        },
        typographyProps: {
            size: 16,
        },
        hoverStyle: {
            color: color("text.link")(),
        },
        css: css`
            margin-bottom: 16px;
        `,
    },
};

const styles = categoryDocumentsStyles;

const CategoryDocuments: React.FC<Props> = ({ category }: Props) => {
    if (!category || !category.properties["documents"]) {
        return null;
    }

    const documents: CategoryDocument[] = JSON.parse(category.properties["documents"]);

    if (!documents || !documents.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {documents.map(document => (
                <Link {...styles.documentLink} key={document.id} target="_blank" href={document.path ?? ""}>
                    {document.name}
                </Link>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDocuments),
    definition: {
        group: "Categories",
        icon: "Banner",
    },
};

export default widgetModule;
